import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

// Pipeline stage definitions
type PageType = 'alibaba' | 'batch' | 'keyword';

interface StageDefinition {
  key: string;
  label: string;
  zh: string;
  group: 'scraping' | 'post' | 'alibaba';
  defaultPages: PageType[];
}

const STAGES: StageDefinition[] = [
  // Scraping methods
  { key: 'scraping_qs', label: 'QuickScraper', zh: '快速爬取', group: 'scraping', defaultPages: ['alibaba', 'batch', 'keyword'] },
  { key: 'scraping_ba', label: 'BrowserAct', zh: '完整渲染', group: 'scraping', defaultPages: ['alibaba'] },
  // Post-processing
  { key: 'parsing', label: 'HTML Parsing', zh: 'HTML 解析', group: 'post', defaultPages: ['alibaba', 'batch', 'keyword'] },
  { key: 'seo_scoring', label: 'SEO Analysis', zh: 'SEO 分析', group: 'post', defaultPages: ['keyword'] },
  { key: 'browseract', label: 'Screenshot', zh: '網頁截圖', group: 'post', defaultPages: [] },
  { key: 'rag_vectorize', label: 'RAG Vectorize', zh: '向量化', group: 'post', defaultPages: [] },
  { key: 'ncb_backup', label: 'NCB Backup', zh: '雲端備份', group: 'post', defaultPages: [] },
  { key: 'serp_analysis', label: 'SERP Analysis', zh: 'SERP 進階分析', group: 'post', defaultPages: ['keyword'] },
  // Alibaba-specific
  { key: 'ali_attrs', label: 'Product Attrs', zh: '產品規格', group: 'alibaba', defaultPages: ['alibaba'] },
  { key: 'ali_product_screenshot', label: 'Product Screenshot', zh: '產品截圖', group: 'alibaba', defaultPages: ['alibaba'] },
  { key: 'ali_iframe', label: 'iframe Description', zh: 'iframe 描述', group: 'alibaba', defaultPages: ['alibaba'] },
  { key: 'ali_iframe_screenshot', label: 'iframe Screenshot', zh: 'iframe 截圖', group: 'alibaba', defaultPages: [] },
  { key: 'ali_image_upload', label: 'Image Upload', zh: '圖片上傳', group: 'alibaba', defaultPages: [] },
];

const GROUP_LABELS: Record<string, string> = {
  scraping: 'Scraping 爬取方式',
  post: 'Post-processing 後處理',
  alibaba: 'Alibaba 阿里專用',
};

interface PipelineOptionsProps {
  page: PageType;
  selectedStages: string[];
  onChange: (stages: string[]) => void;
}

// Get default stages for a page — all visible stages are ON by default
export function getDefaultStages(page: PageType): string[] {
  return getVisibleStages(page).map(s => s.key);
}

// Get visible stages for a page (alibaba group only shows on alibaba page)
function getVisibleStages(page: PageType): StageDefinition[] {
  if (page === 'alibaba') return STAGES;
  return STAGES.filter(s => s.group !== 'alibaba');
}

export default function PipelineOptions({ page, selectedStages, onChange }: PipelineOptionsProps) {
  const visibleStages = getVisibleStages(page);

  // C-series iframe stage keys
  const IFRAME_SERIES = ['ali_iframe', 'ali_iframe_screenshot', 'ali_image_upload'];

  const toggle = (key: string) => {
    let next: string[];

    if (selectedStages.includes(key)) {
      // Unchecking
      next = selectedStages.filter(k => k !== key);
      // Unchecking ali_iframe → also remove C-4 screenshot and C-5 upload
      if (key === 'ali_iframe') {
        next = next.filter(k => !IFRAME_SERIES.includes(k));
      }
    } else {
      // Checking
      next = [...selectedStages, key];
      // Checking ali_iframe → auto-enable the full C series (C-4, C-5)
      if (key === 'ali_iframe') {
        for (const sk of IFRAME_SERIES) {
          if (!next.includes(sk)) next.push(sk);
        }
      }
      // Checking ali_image_upload (C-5) requires ali_iframe
      if (key === 'ali_image_upload' && !next.includes('ali_iframe')) {
        next.push('ali_iframe');
      }
    }

    onChange(next);
  };

  // ali_image_upload disabled when ali_iframe is not selected
  const isDisabled = (key: string): boolean => {
    if (key === 'ali_image_upload' && !selectedStages.includes('ali_iframe')) return true;
    return false;
  };

  // Group stages by group
  const groups = new Map<string, StageDefinition[]>();
  for (const stage of visibleStages) {
    const list = groups.get(stage.group) || [];
    list.push(stage);
    groups.set(stage.group, list);
  }

  return (
    <div className="space-y-2 border-t pt-3 mt-3">
      <Label className="text-muted-foreground text-xs flex items-center gap-1.5">
        Pipeline Stages
        <Badge variant="outline" className="text-[10px] px-1 py-0">{selectedStages.length} selected</Badge>
      </Label>
      {Array.from(groups.entries()).map(([groupKey, stages]) => (
        <div key={groupKey}>
          <span className="text-[11px] text-muted-foreground font-medium">{GROUP_LABELS[groupKey] || groupKey}</span>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 mt-1">
            {stages.map(stage => {
              const disabled = isDisabled(stage.key);
              return (
                <label key={stage.key} className={`flex items-center gap-2 text-sm ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                  <Checkbox
                    checked={selectedStages.includes(stage.key)}
                    onCheckedChange={() => toggle(stage.key)}
                    disabled={disabled}
                  />
                  <span>{stage.label}</span>
                  <span className="text-[10px] text-muted-foreground">{stage.zh}</span>
                </label>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}
